import React from 'react';
import { Button, Grid, Grow, styled } from '@mui/material';

import useSettings from '../hooks/useSettings';
import Icon from '../Icon';
import Text from '../Text';

interface ButtonLinkMenuItemProps {
	/** The items module */
	module: string;
	/** The item data */
	item: any;
	/** The current item table */
	table: string;
	/** The items icons based on the item table */
	tableIcons?: object;
	/** Function which creates the link opened on item click */
	href: (item: any) => string | undefined;
	/** A fallback string if the children is not provided */
	fallback?: string;
}

type ButtonLinkMenuProps = {
	/** The list of items to display in the menu */
	list: any[];
} & Omit<ButtonLinkMenuItemProps, 'item'>;

const StyledButton = styled(Button)(({ theme }) => ({
	maxWidth: '100%',
	alignSelf: 'right',
	textAlign: 'left',
	textTransform: 'none',
	'&:not([role=button])': {
		'&:hover': {
			textDecoration: 'none'
		}
	}
})) as typeof Button;

function MenuItem({ module, item, table, tableIcons, href, fallback }: ButtonLinkMenuItemProps) {
	const { settings: moduleSettings } = useSettings(module);

	const itemTable = item ? item[`${moduleSettings.modu_short}_table`] : null;
	const { settings: tableSettings } = useSettings(itemTable);

	const itemIcon =
		(tableIcons && item ? tableIcons[item[`${moduleSettings.modu_short}_table`]] : null) ||
		moduleSettings.modu_icon;
	const itemTitle = item ? item[`${moduleSettings.modu_short}_title`] : null;
	const tableItemTitle = item ? item[`${tableSettings?.modu_short}_title`] : null;
	const title = item ? (itemTable !== table ? `${itemTitle} (${tableItemTitle})` : itemTitle || fallback) : fallback;

	return (
		<StyledButton
			size="small"
			component="a"
			href={item && href && href(item)}
			color="secondary"
			startIcon={itemIcon && <Icon icon={itemIcon} />}
			disabled={!item}
		>
			<Text className="line-clamp-1 break-all" tooltip={title}>
				{title}
			</Text>
		</StyledButton>
	);
}

/** A text component that, when clicked open a given link */
function ButtonLinkMenu({ module, list, table, tableIcons, href, fallback }: ButtonLinkMenuProps) {
	const { settings } = useSettings(module);

	const [expanded, setExpanded] = React.useState(false);

	const multipleItems = list ? list.length > 1 : false;

	return (
		<div className="flex">
			<div className="flex-grow">
				<MenuItem
					module={module}
					item={list ? list[0] : undefined}
					href={href}
					table={table}
					tableIcons={tableIcons}
					fallback={fallback}
				/>
				{expanded && (
					<Grow
						in={expanded}
						style={{ transformOrigin: 'top center' }}
						{...(expanded ? { timeout: 300 } : {})}
						mountOnEnter
						unmountOnExit
					>
						<Grid container>
							{list?.slice(1).map(item => (
								<Grid key={item[`${settings.modu_short}_uid`]} size={12}>
									<MenuItem
										module={module}
										item={item}
										href={href}
										table={table}
										tableIcons={tableIcons}
									/>
								</Grid>
							))}
						</Grid>
					</Grow>
				)}
			</div>
			{multipleItems && (
				<Button
					className="max-h-32 min-w-32"
					size="small"
					color="secondary"
					onClick={() => setExpanded(state => !state)}
				>
					<Icon icon={expanded ? 'menu-up' : 'menu-down'} size={24} />
				</Button>
			)}
		</div>
	);
}

export default React.memo(ButtonLinkMenu);
